/**
 * Packet parser and serializer for TypeScript
 * Compatible with the C++ packet parser implementation
 */

// Packet types
const PacketType = {
  TYPE_REQUEST: 0x00,
  TYPE_RESPONSE: 0x01,
};

// Operations
const Operation = {
  OP_NULL: 0x00,
  OP_PING: 0x01,
  OP_GET_CONNECTION_JWT: 0x02,
  OP_GET_REVOKE_JWT: 0x03,
  OP_GET_CONNECTION_BASE_URLS: 0x04,
  OP_DISPLAY_CLEAR: 0x10,
  OP_DISPLAY_QRCODE: 0x11,
  OP_DISPLAY_TEXT: 0x12,
  OP_LIGHT: 0x20,
  // v2
  OP_GET_FIRMWARE_INFO: 0x80,
  OP_START_FIRMWARE_UPDATE: 0x81,
  OP_WRITE_FIRMWARE_UPDATE: 0x82,
  OP_END_FIRMWARE_UPDATE: 0x83,
  OP_ABORT_FIRMWARE_UPDATE: 0x84,
  // v3
  OP_GET_DEVICE_INFO: 0x90,
  // factory process
  OP_SHOW_CHARACTER_FP: 0xb0,
  OP_SHOW_QRCODE_FP: 0xb1,
  OP_WRITE_NFC_READ_VALUE_FP: 0xb2,
  OP_START_READ_QRCODE_FP: 0xb3,
  OP_GET_READ_QRCODE_FP: 0xb4,
  OP_STOP_READ_QRCODE_FP: 0xb5,
  OP_WRITE_BLE_READ_VALUE_FP: 0xb6,
  OP_GET_BLE_WRITTEN_VALUE_FP: 0xb7,
  OP_SET_LED_COLOR_FP: 0xb8,
  OP_GET_LIGHT_SENSOR_FP: 0xb9,
  OP_EMIT_SOUND_FP: 0xba,
  OP_START_KEYPAD_TEST_FP: 0xbb,
  OP_GET_KEYPAD_TEST_RESULT_FP: 0xbc,
  OP_FINALIZE_FP: 0xcf,
  // Factory status tracking
  OP_GET_FACTORY_STATUS: 0xc0,
  OP_START_AGING: 0xc1,
  OP_GET_AGING_STATUS: 0xc2,
  // Payregi v2 registration/config via serial
  OP_GET_DEVICE_REGISTRATION_DATA: 0xd0,
  OP_SET_SERIAL_NUMBER: 0xd1,
  OP_GET_SERIAL_NUMBER: 0xd2,
  OP_GET_DEVICE_CONFIG_URL: 0xd3,
  OP_SET_DEVICE_CONFIG: 0xd4,
  OP_SET_DEVICE_SETTINGS: 0xd5,
  //
  OP_GET_BAUDRATE: 0xe0,
  OP_SET_BAUDRATE: 0xe1,
  OP_SYSTEM_RESET: 0xff,
};

// Display text patterns
const DisplayTextPattern = {
  TEXT_SUCCESS_3S: 0x10,
  TEXT_ERROR_REASON_3S: 0x11,
  TEXT_CANCELED_3S: 0x30,
};

// Light color patterns
const LightColorPattern = {
  LIGHT_NONE: 0x00,
  LIGHT_BLUE_BLINK_SLOW: 0x10,
  LIGHT_GREEN_3S: 0x11,
};

// Results
const Result = {
  SUCCEED: 0x00,
  FAILED: 0xff,
};

// Parser states
const ParserState = {
  PREAMBLE_1: 0x00,
  PREAMBLE_2: 0x01,
  TYPE: 0x02,
  SENDER_ID: 0x03,
  DESTINATION_ID: 0x04,
  PACKET_ID: 0x05,
  OPERATOR: 0x06,
  LENGTH_1: 0x07,
  LENGTH_2: 0x08,
  DATA: 0x09,
  CRC_1: 0x0a,
  CRC_2: 0x0b,
  CRC_3: 0x0c,
  CRC_4: 0x0d,
};

// CRC32 lookup table (IEEE 802.3)
const CRC_TABLE = new Uint32Array([
  0x00000000, 0x77073096, 0xee0e612c, 0x990951ba, 0x076dc419, 0x706af48f, 0xe963a535, 0x9e6495a3,
  0x0edb8832, 0x79dcb8a4, 0xe0d5e91e, 0x97d2d988, 0x09b64c2b, 0x7eb17cbd, 0xe7b82d07, 0x90bf1d91,
  0x1db71064, 0x6ab020f2, 0xf3b97148, 0x84be41de, 0x1adad47d, 0x6ddde4eb, 0xf4d4b551, 0x83d385c7,
  0x136c9856, 0x646ba8c0, 0xfd62f97a, 0x8a65c9ec, 0x14015c4f, 0x63066cd9, 0xfa0f3d63, 0x8d080df5,
  0x3b6e20c8, 0x4c69105e, 0xd56041e4, 0xa2677172, 0x3c03e4d1, 0x4b04d447, 0xd20d85fd, 0xa50ab56b,
  0x35b5a8fa, 0x42b2986c, 0xdbbbc9d6, 0xacbcf940, 0x32d86ce3, 0x45df5c75, 0xdcd60dcf, 0xabd13d59,
  0x26d930ac, 0x51de003a, 0xc8d75180, 0xbfd06116, 0x21b4f4b5, 0x56b3c423, 0xcfba9599, 0xb8bda50f,
  0x2802b89e, 0x5f058808, 0xc60cd9b2, 0xb10be924, 0x2f6f7c87, 0x58684c11, 0xc1611dab, 0xb6662d3d,
  0x76dc4190, 0x01db7106, 0x98d220bc, 0xefd5102a, 0x71b18589, 0x06b6b51f, 0x9fbfe4a5, 0xe8b8d433,
  0x7807c9a2, 0x0f00f934, 0x9609a88e, 0xe10e9818, 0x7f6a0dbb, 0x086d3d2d, 0x91646c97, 0xe6635c01,
  0x6b6b51f4, 0x1c6c6162, 0x856530d8, 0xf262004e, 0x6c0695ed, 0x1b01a57b, 0x8208f4c1, 0xf50fc457,
  0x65b0d9c6, 0x12b7e950, 0x8bbeb8ea, 0xfcb9887c, 0x62dd1ddf, 0x15da2d49, 0x8cd37cf3, 0xfbd44c65,
  0x4db26158, 0x3ab551ce, 0xa3bc0074, 0xd4bb30e2, 0x4adfa541, 0x3dd895d7, 0xa4d1c46d, 0xd3d6f4fb,
  0x4369e96a, 0x346ed9fc, 0xad678846, 0xda60b8d0, 0x44042d73, 0x33031de5, 0xaa0a4c5f, 0xdd0d7cc9,
  0x5005713c, 0x270241aa, 0xbe0b1010, 0xc90c2086, 0x5768b525, 0x206f85b3, 0xb966d409, 0xce61e49f,
  0x5edef90e, 0x29d9c998, 0xb0d09822, 0xc7d7a8b4, 0x59b33d17, 0x2eb40d81, 0xb7bd5c3b, 0xc0ba6cad,
  0xedb88320, 0x9abfb3b6, 0x03b6e20c, 0x74b1d29a, 0xead54739, 0x9dd277af, 0x04db2615, 0x73dc1683,
  0xe3630b12, 0x94643b84, 0x0d6d6a3e, 0x7a6a5aa8, 0xe40ecf0b, 0x9309ff9d, 0x0a00ae27, 0x7d079eb1,
  0xf00f9344, 0x8708a3d2, 0x1e01f268, 0x6906c2fe, 0xf762575d, 0x806567cb, 0x196c3671, 0x6e6b06e7,
  0xfed41b76, 0x89d32be0, 0x10da7a5a, 0x67dd4acc, 0xf9b9df6f, 0x8ebeeff9, 0x17b7be43, 0x60b08ed5,
  0xd6d6a3e8, 0xa1d1937e, 0x38d8c2c4, 0x4fdff252, 0xd1bb67f1, 0xa6bc5767, 0x3fb506dd, 0x48b2364b,
  0xd80d2bda, 0xaf0a1b4c, 0x36034af6, 0x41047a60, 0xdf60efc3, 0xa867df55, 0x316e8eef, 0x4669be79,
  0xcb61b38c, 0xbc66831a, 0x256fd2a0, 0x5268e236, 0xcc0c7795, 0xbb0b4703, 0x220216b9, 0x5505262f,
  0xc5ba3bbe, 0xb2bd0b28, 0x2bb45a92, 0x5cb36a04, 0xc2d7ffa7, 0xb5d0cf31, 0x2cd99e8b, 0x5bdeae1d,
  0x9b64c2b0, 0xec63f226, 0x756aa39c, 0x026d930a, 0x9c0906a9, 0xeb0e363f, 0x72076785, 0x05005713,
  0x95bf4a82, 0xe2b87a14, 0x7bb12bae, 0x0cb61b38, 0x92d28e9b, 0xe5d5be0d, 0x7cdcefb7, 0x0bdbdf21,
  0x86d3d2d4, 0xf1d4e242, 0x68ddb3f8, 0x1fda836e, 0x81be16cd, 0xf6b9265b, 0x6fb077e1, 0x18b74777,
  0x88085ae6, 0xff0f6a70, 0x66063bca, 0x11010b5c, 0x8f659eff, 0xf862ae69, 0x616bffd3, 0x166ccf45,
  0xa00ae278, 0xd70dd2ee, 0x4e048354, 0x3903b3c2, 0xa7672661, 0xd06016f7, 0x4969474d, 0x3e6e77db,
  0xaed16a4a, 0xd9d65adc, 0x40df0b66, 0x37d83bf0, 0xa9bcae53, 0xdebb9ec5, 0x47b2cf7f, 0x30b5ffe9,
  0xbdbdf21c, 0xcabac28a, 0x53b39330, 0x24b4a3a6, 0xbad03605, 0xcdd70693, 0x54de5729, 0x23d967bf,
  0xb3667a2e, 0xc4614ab8, 0x5d681b02, 0x2a6f2b94, 0xb40bbe37, 0xc30c8ea1, 0x5a05df1b, 0x2d02ef8d,
]);

/**
 * Calculate CRC32 checksum (IEEE 802.3)
 */
function calcCrc32(buffer: Uint8Array, length: number): number {
  let crc = 0xffffffff;
  for (let i = 0; i < length; i++) {
    crc = CRC_TABLE[(crc ^ buffer[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0; // Ensure unsigned 32-bit
}

/**
 * Packet class representing a protocol packet
 */
class Packet {
  type: number;
  senderId: number;
  destinationId: number;
  packetId: number;
  op: number;
  len: number;
  data: Uint8Array;

  constructor() {
    this.type = PacketType.TYPE_REQUEST;
    this.senderId = 0;
    this.destinationId = 1;
    this.packetId = 0;
    this.op = 0;
    this.len = 0;
    this.data = new Uint8Array(0);
  }

  serialize(): Uint8Array {
    return PacketManager.serializePacket(this);
  }
}

/**
 * Cache buffer for managing incoming data
 */
class CacheBuffer {
  buffer: Uint8Array;
  ptr: number;

  constructor() {
    this.buffer = new Uint8Array(0);
    this.ptr = 0;
  }

  reset(): void {
    this.buffer = new Uint8Array(0);
    this.ptr = 0;
  }

  push(data: number): void {
    const newBuffer = new Uint8Array(this.buffer.length + 1);
    newBuffer.set(this.buffer);
    newBuffer[this.buffer.length] = data;
    this.buffer = newBuffer;
  }

  pushBack(src: Uint8Array): void {
    const len = src.length;
    const newBuffer = new Uint8Array(this.buffer.length + len);
    newBuffer.set(this.buffer);
    newBuffer.set(src.slice(0, len), this.buffer.length);
    this.buffer = newBuffer;
  }

  peek(): number {
    if (this.ptr >= this.buffer.length) {
      return -1; // NOT_FOUND
    }
    return this.buffer[this.ptr++];
  }

  drop(): void {
    if (this.buffer.length > 0) {
      this.buffer = this.buffer.slice(1);
      this.ptr = 0;
    }
  }

  take(): void {
    if (this.ptr > 0) {
      this.buffer = this.buffer.slice(this.ptr);
      this.ptr = 0;
    }
  }
}

/**
 * Packet manager for parsing and serializing packets
 */
class PacketManager {
  rxBuffer: CacheBuffer;
  txBuffer: CacheBuffer;
  state: number;
  type: number;
  senderId: number;
  destinationId: number;
  packetId: number;
  op: number;
  temp: number;
  crc: number;
  len: number;
  dataIdx: number;

  constructor() {
    this.rxBuffer = new CacheBuffer();
    this.txBuffer = new CacheBuffer();
    this.state = ParserState.PREAMBLE_1;

    // Parser state variables
    this.type = PacketType.TYPE_REQUEST;
    this.senderId = 0;
    this.destinationId = 1;
    this.packetId = 0;
    this.op = 0;
    this.temp = 0;
    this.crc = 0;
    this.len = 0;
    this.dataIdx = 0;
  }

  parsePacket(): Packet | null {
    let p = new Packet();
    const success = this.parseInPacket(p);
    if (success) {
      return p;
    } else {
      return null;
    }
  }

  /**
   * Parse incoming packet data
   * @param {Packet} packet - Packet object to populate
   * @returns {number} 1 if packet parsed successfully, 0 otherwise
   */
  parseInPacket(packet: Packet): boolean {
    while (true) {
      const x = this.rxBuffer.peek();
      if (x < 0) {
        break;
      }
      const c = x & 0xff;

      switch (this.state) {
        case ParserState.PREAMBLE_1:
          if (c === 0xaa) {
            this.state = ParserState.PREAMBLE_2;
          } else {
            this.rxBuffer.drop();
            this.state = ParserState.PREAMBLE_1;
          }
          break;

        case ParserState.PREAMBLE_2:
          if (c === 0x55) {
            this.state = ParserState.TYPE;
          } else {
            this.rxBuffer.drop();
            this.state = ParserState.PREAMBLE_1;
          }
          break;

        case ParserState.TYPE:
          if (c === PacketType.TYPE_REQUEST || c === PacketType.TYPE_RESPONSE) {
            this.type = c;
            this.state = ParserState.SENDER_ID;
          } else {
            this.rxBuffer.drop();
            this.state = ParserState.PREAMBLE_1;
          }
          break;

        case ParserState.SENDER_ID:
          this.senderId = c;
          this.state = ParserState.DESTINATION_ID;
          break;

        case ParserState.DESTINATION_ID:
          this.destinationId = c;
          this.state = ParserState.PACKET_ID;
          break;

        case ParserState.PACKET_ID:
          this.packetId = c;
          this.state = ParserState.OPERATOR;
          break;

        case ParserState.OPERATOR:
          this.op = c;
          this.state = ParserState.LENGTH_1;
          break;

        case ParserState.LENGTH_1:
          this.temp = c;
          this.state = ParserState.LENGTH_2;
          break;

        case ParserState.LENGTH_2:
          this.temp = (c << 8) | this.temp;
          if (this.temp + 13 > 0x0fff) {
            // Length too long, this must not be a packet
            this.rxBuffer.drop();
            this.state = ParserState.PREAMBLE_1;
          } else {
            this.len = this.temp;
            if (this.len > 0) {
              this.state = ParserState.DATA;
            } else {
              this.state = ParserState.CRC_1;
            }
            this.dataIdx = 0;
          }
          break;

        case ParserState.DATA:
          this.dataIdx++;
          if (this.dataIdx >= this.len) {
            this.state = ParserState.CRC_1;
          }
          break;

        case ParserState.CRC_1:
          this.crc = c;
          this.state = ParserState.CRC_2;
          break;

        case ParserState.CRC_2:
          this.temp = c;
          this.crc |= this.temp << 8;
          this.state = ParserState.CRC_3;
          break;

        case ParserState.CRC_3:
          this.temp = c;
          this.crc |= this.temp << 16;
          this.state = ParserState.CRC_4;
          break;

        case ParserState.CRC_4:
          this.temp = c;
          this.crc |= this.temp << 24;
          this.crc = this.crc >>> 0; // Ensure unsigned 32-bit

          const crc32Calc = calcCrc32(this.rxBuffer.buffer, this.len + 9);
          if (this.crc === crc32Calc) {
            // Packet parsed successfully
            packet.type = this.type;
            packet.senderId = this.senderId;
            packet.destinationId = this.destinationId;
            packet.packetId = this.packetId;
            packet.op = this.op;
            packet.len = this.len;

            // Extract data
            packet.data = new Uint8Array(this.len);
            for (let i = 0; i < this.len; i++) {
              packet.data[i] = this.rxBuffer.buffer[9 + i];
            }

            this.rxBuffer.take();
            this.state = ParserState.PREAMBLE_1;
            return true;
          } else {
            const crcHex = ('00000000' + this.crc.toString(16)).slice(-8);
            const calcHex = ('00000000' + crc32Calc.toString(16)).slice(-8);
            console.log(`CRC not match ${crcHex} != ${calcHex}`);
            this.rxBuffer.drop();
            this.state = ParserState.PREAMBLE_1;
          }
          break;
      }
    }
    return false;
  }

  /**
   * Write bytes to the receive buffer
   * @param {Uint8Array} src - Source data
   * @returns {number} Number of bytes written
   */
  writeBytes(src: Uint8Array): number {
    this.rxBuffer.pushBack(src);
    return src.length;
  }

  /**
   * Write a single byte to the receive buffer
   * @param {number} b - Byte to write
   */
  writeByte(b: number): void {
    this.rxBuffer.push(b);
  }

  /**
   * Reset the packet manager
   */
  reset(): void {
    this.rxBuffer.reset();
    this.state = ParserState.PREAMBLE_1;
  }

  /**
   * Serialize a packet to bytes
   * @param {Packet} packet - Packet to serialize
   * @returns {number} Number of bytes written, 0 if buffer too small
   */
  static serializePacket(packet: Packet): Uint8Array {
    const dest = new Uint8Array(packet.len + 13);

    // Set preamble
    dest[0] = 0xaa;
    dest[1] = 0x55;

    // Set packet fields
    dest[2] = packet.type;
    dest[3] = packet.senderId;
    dest[4] = packet.destinationId;
    dest[5] = packet.packetId;
    dest[6] = packet.op;
    dest[7] = packet.len & 0xff;
    dest[8] = (packet.len >> 8) & 0xff;

    // Copy data
    for (let i = 0; i < packet.len; i++) {
      dest[9 + i] = packet.data[i];
    }

    // Calculate and append CRC32
    const crc32 = calcCrc32(dest, packet.len + 9);
    dest[packet.len + 9] = crc32 & 0xff;
    dest[packet.len + 10] = (crc32 >> 8) & 0xff;
    dest[packet.len + 11] = (crc32 >> 16) & 0xff;
    dest[packet.len + 12] = (crc32 >> 24) & 0xff;

    return dest;
  }
}

const Environment = {
  PRODUCTION: 0,
  SANDBOX: 1,
  TOPIC1: 2,
  QA: 3,
  DEVELOP: 4,
  LOADTEST: 5,
};

const FactoryStatus = {
  NOT_STARTED: 0, // QA not done
  QA_COMPLETED: 1, // QA done, ready for aging or registration
  AGING_IN_PROGRESS: 2, // Aging started (for future use)
  AGING_COMPLETED: 3, // Aging done, ready for registration (for future use)
  FULLY_REGISTERED: 4, // Serial number assigned
};

// Export classes and constants
export {
  DisplayTextPattern,
  Environment,
  FactoryStatus,
  LightColorPattern,
  Operation,
  Packet,
  PacketManager,
  PacketType,
  Result,
  calcCrc32,
};
